export interface TablePngColumn {
  label: string;
  align?: 'start' | 'center' | 'end';
  /** Minimum content width in px (before horizontal padding). */
  minWidth?: number;
}

export interface DownloadTablePngOptions {
  title?: string;
  footer?: string;
  columns: TablePngColumn[];
  rows: string[][];
  fileName: string;
  /** Reverse column order for RTL (rightmost logical column drawn on the right). */
  rtl?: boolean;
}

const FONT =
  '12px Vazirmatn, Vazir, system-ui, -apple-system, Segoe UI, sans-serif';
const HEADER_FONT =
  '700 12px Vazirmatn, Vazir, system-ui, -apple-system, Segoe UI, sans-serif';
const TITLE_FONT =
  '700 14px Vazirmatn, Vazir, system-ui, -apple-system, Segoe UI, sans-serif';
const FOOTER_FONT =
  '11px Vazirmatn, Vazir, system-ui, -apple-system, Segoe UI, sans-serif';

const CELL_PAD_X = 10;

function measure(ctx: CanvasRenderingContext2D, text: string, font: string): number {
  ctx.font = font;
  return ctx.measureText(text).width;
}

/** RTL = reverse columns + row cells once; draw always left→right with matching indices. */
function prepareDisplayData(options: DownloadTablePngOptions): {
  columns: TablePngColumn[];
  rows: string[][];
} {
  if (!options.rtl) {
    return { columns: options.columns, rows: options.rows };
  }
  return {
    columns: [...options.columns].reverse(),
    rows: options.rows.map(row => [...row].reverse()),
  };
}

/**
 * Renders a data table to PNG and triggers a browser download.
 * Cells are always placed on a single contiguous grid (no RTL offset bugs).
 */
export async function downloadTablePng(options: DownloadTablePngOptions): Promise<void> {
  await document.fonts.ready;

  const { columns, rows } = prepareDisplayData(options);
  const pad = 20;
  const titleGap = 12;
  const footerGap = 10;
  const border = '#d8dde6';
  const headerBg = '#eef1f6';
  const zebra = '#f8f9fb';
  const text = '#1a1f2b';
  const muted = '#5c6573';
  const footerColor = '#9aa1b1';

  const scratch = document.createElement('canvas');
  const measureCtx = scratch.getContext('2d');
  if (!measureCtx) return;

  const colWidths = columns.map((col, ci) => {
    let w = measure(measureCtx, col.label, HEADER_FONT);
    for (const row of rows) {
      w = Math.max(w, measure(measureCtx, row[ci] ?? '', FONT));
    }
    const contentW = Math.max(Math.ceil(w), col.minWidth ?? 0);
    return contentW + CELL_PAD_X * 2;
  });

  const colOffsets: number[] = [];
  let xCursor = pad;
  for (const w of colWidths) {
    colOffsets.push(xCursor);
    xCursor += w;
  }

  const tableWidth = colWidths.reduce((sum, w) => sum + w, 0);
  const rowHeight = 32;
  const headerHeight = 36;
  const titleHeight = options.title ? 22 + titleGap : 0;
  const footerHeight = options.footer ? 18 + footerGap : 0;
  const tableHeight = headerHeight + rows.length * rowHeight;
  const totalWidth = tableWidth + pad * 2;
  const totalHeight = tableHeight + titleHeight + footerHeight + pad * 2;

  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = totalWidth * scale;
  canvas.height = totalHeight * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(scale, scale);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, totalWidth, totalHeight);

  let y = pad;
  if (options.title) {
    ctx.font = TITLE_FONT;
    ctx.fillStyle = text;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = options.rtl ? 'right' : 'left';
    ctx.fillText(options.title, options.rtl ? totalWidth - pad : pad, y + 16);
    y += titleHeight;
  }

  const drawCell = (
    ci: number,
    rowTop: number,
    height: number,
    value: string,
    isHeader: boolean,
    fill?: string,
  ) => {
    const colWidth = colWidths[ci]!;
    const x = colOffsets[ci]!;
    ctx.fillStyle = fill ?? '#ffffff';
    ctx.fillRect(x, rowTop, colWidth, height);
    ctx.strokeStyle = border;
    ctx.strokeRect(x + 0.5, rowTop + 0.5, colWidth - 1, height - 1);

    const align = columns[ci]?.align ?? 'center';
    ctx.font = isHeader ? HEADER_FONT : FONT;
    ctx.fillStyle = isHeader ? muted : text;
    ctx.textBaseline = 'middle';

    let textX = x + colWidth / 2;
    let textAlign: CanvasTextAlign = 'center';
    if (align === 'start') {
      textX = x + CELL_PAD_X;
      textAlign = 'left';
    } else if (align === 'end') {
      textX = x + colWidth - CELL_PAD_X;
      textAlign = 'right';
    }
    ctx.textAlign = textAlign;

    ctx.save();
    ctx.beginPath();
    ctx.rect(x + 1, rowTop + 1, colWidth - 2, height - 2);
    ctx.clip();
    ctx.fillText(value, textX, rowTop + height / 2);
    ctx.restore();
  };

  for (let ci = 0; ci < columns.length; ci += 1) {
    drawCell(ci, y, headerHeight, columns[ci]!.label, true, headerBg);
  }
  y += headerHeight;

  rows.forEach((row, rowIndex) => {
    const fill = rowIndex % 2 === 1 ? zebra : '#ffffff';
    for (let ci = 0; ci < columns.length; ci += 1) {
      drawCell(ci, y, rowHeight, row[ci] ?? '', false, fill);
    }
    y += rowHeight;
  });

  if (options.footer) {
    y += footerGap;
    ctx.font = FOOTER_FONT;
    ctx.fillStyle = footerColor;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText(options.footer, totalWidth / 2, y);
  }

  await new Promise<void>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('PNG export failed'));
        return;
      }
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = options.fileName.endsWith('.png') ? options.fileName : `${options.fileName}.png`;
      anchor.click();
      URL.revokeObjectURL(url);
      resolve();
    }, 'image/png');
  });
}
